/** Debug despawn scene — lists live enemies with HP and AI state, allowing selective or bulk removal. */
import Phaser from 'phaser';
import { Enemy } from '../../entities/Enemy';
import { GameScene } from '../GameScene';
import { crispText, crispButton, setupCrispCamera } from '../../ui/CrispUtil';
import { Font, Hex, TextStyle, ButtonColor } from '../../ui/Theme';
import { GAME_WIDTH } from '../../utils/Constants';

const LINE_HEIGHT = 24;
const MAX_VISIBLE = 20;

interface DespawnRow {
  enemy: Enemy;
  selected: boolean;
  text: Phaser.GameObjects.Text;
}

/**
 * Reads the live enemy list from EnemyManager and renders up to MAX_VISIBLE rows. Clicking a row
 * toggles its selected state; DESPAWN SELECTED removes chosen enemies, DESPAWN ALL clears the field.
 */
export class DebugDespawnScene extends Phaser.Scene {
  private rows: DespawnRow[] = [];

  constructor() {
    super({ key: 'DebugDespawnScene' });
  }

  create(): void {
    this.rows = [];
    setupCrispCamera(this);
    const width = GAME_WIDTH;

    this.cameras.main.setBackgroundColor(Hex.sceneBg);

    crispText(this, width / 2, 30, 'DESPAWN ENEMIES', TextStyle.debugTitle).setOrigin(0.5);

    this.input.keyboard!.on('keydown-ESC', () => this.scene.start('DebugSettingsScene'));

    const gs = this.scene.get('GameScene') as GameScene;
    const enemies = gs?.enemyManager ? [...gs.enemyManager.all] : [];

    const hasCorpses = gs?.enemyManager ? gs.enemyManager.activeCorpses.length > 0 : false;

    if (enemies.length === 0) {
      crispText(this, width / 2, 100, 'No enemies spawned.', TextStyle.smallMuted).setOrigin(0.5);
      if (hasCorpses) {
        crispButton(this, width / 2, 140, 'CLEAR BODIES',
          Font.sizeSmall, ButtonColor.danger, () => this.doClearCorpses());
      }
      return;
    }

    const startY = 70;
    const visibleCount = Math.min(enemies.length, MAX_VISIBLE);

    for (let i = 0; i < visibleCount; i++) {
      const enemy = enemies[i];
      const tag = enemy.ai.tag;

      const hp = enemy.healthComponent;
      const state = enemy.ai.state.toUpperCase();
      const label = `- ${tag}  HP: ${Math.round(hp.hp)}/${hp.max}  ${state}`;

      const y = startY + i * LINE_HEIGHT;
      const text = crispText(this, width / 2, y, label, TextStyle.small)
        .setOrigin(0.5).setInteractive({ useHandCursor: true });

      const row: DespawnRow = { enemy, selected: false, text };
      this.rows.push(row);

      text.on('pointerdown', () => {
        row.selected = !row.selected;
        this.refreshRow(row);
      });
      text.on('pointerover', () => text.setColor('#ffffff'));
      text.on('pointerout', () => text.setColor(row.selected ? '#ff6666' : Hex.textPrimary));
    }

    // Summary + overflow
    const summaryY = startY + visibleCount * LINE_HEIGHT + 10;
    const summaryText = crispText(this, width / 2, summaryY, '', TextStyle.smallMuted).setOrigin(0.5);
    this.updateSummary(summaryText, enemies.length);

    if (enemies.length > MAX_VISIBLE) {
      crispText(this, width / 2, summaryY + 18, `(${enemies.length - MAX_VISIBLE} more not shown)`, TextStyle.smallMuted).setOrigin(0.5);
    }

    // Buttons
    const btnY = summaryY + 40;

    crispButton(this, width / 2 - 120, btnY, 'DESPAWN SELECTED',
      Font.sizeSmall, ButtonColor.despawn, () => this.doDespawnSelected());
    crispButton(this, width / 2 + 60, btnY, 'DESPAWN ALL',
      Font.sizeSmall, ButtonColor.danger, () => this.doDespawnAll());

    crispButton(this, width / 2, btnY + 30, 'CLEAR BODIES',
      Font.sizeSmall, ButtonColor.danger, () => this.doClearCorpses());

  }

  private refreshRow(row: DespawnRow): void {
    const tag = row.enemy.ai.tag;
    const hp = row.enemy.healthComponent;
    const state = row.enemy.ai.state.toUpperCase();
    const mark = row.selected ? 'X' : '-';

    row.text.setText(`${mark} ${tag}  HP: ${Math.round(hp.hp)}/${hp.max}  ${state}`);
    row.text.setColor(row.selected ? '#ff6666' : Hex.textPrimary);
  }

  private updateSummary(text: Phaser.GameObjects.Text, total: number): void {
    const selected = this.rows.filter(r => r.selected).length;
    text.setText(`${total} enemies (${selected} selected)`);
  }

  private doDespawnSelected(): void {
    const gs = this.scene.get('GameScene') as GameScene;
    if (!gs?.enemyManager) return;

    const toRemove = this.rows.filter(r => r.selected).map(r => r.enemy);
    for (const enemy of toRemove) {
      gs.enemyManager.removeEnemy(enemy);
    }

    this.scene.start('DebugSettingsScene');
  }

  private doDespawnAll(): void {
    const gs = this.scene.get('GameScene') as GameScene;
    if (!gs?.enemyManager) return;

    const all = [...gs.enemyManager.all];
    for (const enemy of all) {
      gs.enemyManager.removeEnemy(enemy);
    }
    gs.enemyManager.clearCorpses();

    this.scene.start('DebugSettingsScene');
  }

  private doClearCorpses(): void {
    const gs = this.scene.get('GameScene') as GameScene;
    if (!gs?.enemyManager) return;

    gs.enemyManager.clearCorpses();
    this.scene.start('DebugSettingsScene');
  }
}
